import { api } from './client'

// MOCK-ONLY API — the real backend has no follow-up/marketing engine yet.
// White-labeled as "Marketing Supercharge" in the UI (never "GoHighLevel").
// Customer-keyed: the drip, the two-way thread, and the consent/stop control
// all hang off a customer id. Endpoints live only in backend-mock
// (routes/marketing.ts). Wire types live here until the real BE grows a
// Customer/Contact entity + follow-up service.

export type CustomerEventKind =
  | 'enrolled'
  | 'sms_sent'
  | 'email_sent'
  | 'delivered'
  | 'opened'
  | 'replied'
  | 'agent_reply'
  | 'unresponsive'
  | 'stopped'
  | 'opted_out'
  | 'scheduled'
  | 'converted'

export type CustomerConsentState =
  | 'subscribed'
  | 'paused'
  | 'handled_offline'
  | 'opted_out'

export interface CustomerEvent {
  id: string
  customerId: number
  estimateId: number | null
  kind: CustomerEventKind
  channel: 'sms' | 'email' | null
  direction: 'outbound' | 'inbound' | null
  campaignStep: number | null
  stepLabel: string | null
  body: string | null
  occurredAt: string
}

export interface CustomerEnrollment {
  customerId: number
  estimateId: number | null
  campaignName: string
  channel: 'sms' | 'email'
  status: 'active' | 'converted' | 'unresponsive' | 'stopped' | 'not_started'
  currentStep: number
  totalSteps: number
  nextTouchAt: string | null
  enrolledAt: string | null
  /** The customer's self-serve booking page (the link the texts carry). */
  bookingUrl: string | null
}

export interface CustomerActivityResponse {
  events: CustomerEvent[]
  enrollment: CustomerEnrollment
  consent: CustomerConsentState
}

export const customerActivityApi = {
  // GET /marketing/customer/:id/activity — derived timeline + enrollment + consent.
  activity: (customerId: number | string) =>
    api.get<CustomerActivityResponse>(`/marketing/customer/${customerId}/activity`),

  // POST /marketing/customer/:id/messages — agent texts back (mock appends agent_reply).
  sendMessage: (customerId: number | string, body: string, estimateId?: number | null) =>
    api.post<{ id: number }>(`/marketing/customer/${customerId}/messages`, {
      body,
      estimateId: estimateId ?? null,
    }),

  // POST /marketing/customer/:id/consent — the anti-spam control (halt / resume).
  setConsent: (
    customerId: number | string,
    state: CustomerConsentState,
    reason?: string,
  ) =>
    api.post<{ customerId: number; state: CustomerConsentState }>(
      `/marketing/customer/${customerId}/consent`,
      { state, reason: reason ?? null },
    ),
}
